<template>
  <div>
    <div
      v-if="showPrivateLease && privateLeaseResponse"
      class="vcr-private-lease-details vcr-page__section"
    >
      <h2 class="vcr-page__header">
        {{ i18n.t('/Vcr/Private Lease/Title') }}
      </h2>
      <div class="vcr-private-lease-details__sections">
        <div class="vcr-private-lease-details__section">
          <dl>
            <dt>{{ i18n.t('/Vcr/Private Lease/Duration Label') }}</dt>
            <dd>
              {{ privateLeaseParams.duration }}
              {{ retrievePrivateLeaseLabel('durationpostfix') }}
            </dd>

            <dt>
              {{ i18n.t('/Vcr/Private Lease/Yearly Km Label') }}
            </dt>
            <dd>
              {{ i18n.n(privateLeaseParams.yearlyKm) }}
            </dd>

            <dt>
              {{ i18n.t('/Vcr/Private Lease/Own risk level Label') }}
            </dt>
            <dd>{{ selectedOwnRisk }}</dd>
            <template v-if="privateLeaseDiscounts">
              <dt>
                {{ i18n.t('/Vcr/Private Lease/Standard Foak Amount Label') }}
              </dt>
              <dd>
                {{
                  i18n.n(
                    CurrencyHelper.fromString(
                      privateLeaseDiscounts.foak.standardFoak.amountExclVat
                    ),
                    'currencyRounded'
                  )
                }}
              </dd>
              <dt>
                {{ i18n.t('/Vcr/Private Lease/Standard Foak Percentage Label') }}
              </dt>
              <dd>
                {{ `${privateLeaseDiscounts.foak.standardFoak.percentage}%` }}
              </dd>

              <dt>
                {{ i18n.t('/Vcr/Private Lease/Extra Foak Label') }}
              </dt>
              <dd>
                {{
                  i18n.n(
                    CurrencyHelper.fromString(privateLeaseDiscounts.foak.extraFoak.amountExclVat),
                    'currencyRounded'
                  )
                }}
              </dd>

              <dt>
                {{ i18n.t('/Vcr/Private Lease/Extra Foak Amount Label') }}
              </dt>
              <dd>
                {{
                  i18n.n(
                    CurrencyHelper.fromString(
                      privateLeaseDiscounts.foak.extraFoakAmount.amountExclVat
                    ),
                    'currencyRounded'
                  )
                }}
              </dd>

              <dt>{{ i18n.t('/Vcr/Private Lease/Total Foak Label') }}</dt>
              <dd>
                {{
                  i18n.n(
                    CurrencyHelper.fromString(privateLeaseDiscounts.foak.totalFoakExclVat),
                    'currency'
                  )
                }}
              </dd>

              <dt>{{ i18n.t('/Vcr/Private Lease/Dealer Discount Label') }}</dt>
              <dd>
                {{
                  i18n.n(
                    CurrencyHelper.fromString(
                      privateLeaseDiscounts.dealer.totalExtraDealerDiscountExclVat
                    ),
                    'currencyRounded'
                  )
                }}
              </dd>
            </template>

            <dt>{{ i18n.t('/Vcr/Private Lease/Collectivity Code Label') }}</dt>
            <dd>
              {{ privateLeaseParams.collectivityCode ? privateLeaseParams.collectivityCode : '-' }}
            </dd>

            <dt>{{ i18n.t('/Vcr/Private Lease/Lease Price Label') }}</dt>
            <dd>
              {{ hasMonthlyPrePayment ? rateAmount : privateLeasePrice }}
              {{ retrievePrivateLeaseLabel('summaryratepostfix') }}
            </dd>

            <template v-if="hasMonthlyPrePayment">
              <dt>{{ retrievePrivateLeaseLabel('privatelease/rateprepaymentmonthlylabel') }}</dt>
              <dd data-qa="vcr-prepayment-monthly">
                {{ formattedRatePrePaymentMonthly }}
                {{ retrievePrivateLeaseLabel('summaryratepostfix') }}
              </dd>

              <dt class="vcr-lease-total-highlight">
                {{ retrievePrivateLeaseLabel('privatelease/rateamountafterprepaymentlabel') }}
              </dt>
              <dd class="vcr-lease-total-highlight" data-qa="vcr-price-after-prepayment">
                {{ privateLeasePrice }}
                {{ retrievePrivateLeaseLabel('summaryratepostfix') }}
              </dd>
            </template>

            <dt>Correcties</dt>
            <dd>
              <div
                v-if="
                  privateLeaseParams.multipleDiscounts &&
                  privateLeaseParams.multipleDiscounts.length > 0
                "
              >
                <div
                  v-for="discount in privateLeaseParams.multipleDiscounts"
                  :key="discount.id"
                  class="applied-discount-item"
                >
                  <span class="discount-description">{{ discount.description }}</span>
                  <span v-if="discount.type === 'amount'" class="discount-tax-type"
                    >{{ discount.taxType }}.</span
                  >
                  <span class="discount-value">
                    <span v-if="discount.type === 'amount'">€</span>
                    <span v-else>%</span>
                    {{ formatDiscountValue(discount.value) }}
                  </span>
                </div>
              </div>
              <span v-else>-</span>
            </dd>
          </dl>
        </div>
      </div>
      <lease-overview :lease-type="leaseTypes.PRIVATE_LEASE" />
    </div>

    <!-- Operational Lease Details -->
    <div
      v-if="showOperationalLease && operationalLeaseResponse"
      class="vcr-operational-lease-details vcr-page__section"
    >
      <h2 class="vcr-page__header">
        {{ i18n.t('/Vcr/Operational Lease/Title') }}
      </h2>
      <div class="vcr-operational-lease-details__sections">
        <div class="vcr-operational-lease-details__section">
          <dl>
            <dt>{{ i18n.t('/Vcr/Operational Lease/Duration Label') }}</dt>
            <dd>
              {{ operationalLeaseParams.duration }}
              {{ retrievePrivateLeaseLabel('durationpostfix') }}
            </dd>

            <dt>
              {{ i18n.t('/Vcr/Operational Lease/Yearly Km Label') }}
            </dt>
            <dd>
              {{ i18n.n(operationalLeaseParams.yearlyKm) }}
            </dd>

            <template v-if="operationalLeaseDiscounts">
              <dt>
                {{ i18n.t('/Vcr/Operational Lease/Standard Foak Amount Label') }}
              </dt>
              <dd>
                {{
                  i18n.n(
                    CurrencyHelper.fromString(
                      operationalLeaseDiscounts.foak.standardFoak.amountExclVat
                    ),
                    'currencyRounded'
                  )
                }}
              </dd>
              <dt>
                {{ i18n.t('/Vcr/Operational Lease/Standard Foak Percentage Label') }}
              </dt>
              <dd>
                {{ `${operationalLeaseDiscounts.foak.standardFoak.percentage}%` }}
              </dd>

              <dt>
                {{ i18n.t('/Vcr/Operational Lease/Extra Foak Label') }}
              </dt>
              <dd>
                {{
                  i18n.n(
                    CurrencyHelper.fromString(
                      operationalLeaseDiscounts.foak.extraFoak.amountExclVat
                    ),
                    'currencyRounded'
                  )
                }}
              </dd>

              <dt>
                {{ i18n.t('/Vcr/Operational Lease/Extra Foak Amount Label') }}
              </dt>
              <dd>
                {{
                  i18n.n(
                    CurrencyHelper.fromString(
                      operationalLeaseDiscounts.foak.extraFoakAmount.amountExclVat
                    ),
                    'currencyRounded'
                  )
                }}
              </dd>

              <dt>{{ i18n.t('/Vcr/Operational Lease/Total Foak Label') }}</dt>
              <dd>
                {{
                  i18n.n(
                    CurrencyHelper.fromString(operationalLeaseDiscounts.foak.totalFoakExclVat),
                    'currency'
                  )
                }}
              </dd>

              <dt>{{ i18n.t('/Vcr/Operational Lease/Dealer Discount Label') }}</dt>
              <dd>
                {{
                  i18n.n(
                    CurrencyHelper.fromString(
                      operationalLeaseDiscounts.dealer.totalExtraDealerDiscountExclVat
                    ),
                    'currencyRounded'
                  )
                }}
              </dd>
            </template>

            <dt>{{ i18n.t('/Vcr/Operational Lease/Collectivity Code Label') }}</dt>
            <dd>
              {{
                operationalLeaseParams.collectivityCode
                  ? operationalLeaseParams.collectivityCode
                  : '-'
              }}
            </dd>

            <dt>{{ i18n.t('/Vcr/Operational Lease/Lease Price Label') }}</dt>
            <dd>
              {{ operationalLeasePrice }}
              {{ retrievePrivateLeaseLabel('summaryratepostfix') }}
            </dd>

            <dt>Correcties</dt>
            <dd>
              <div
                v-if="
                  operationalLeaseParams.multipleDiscounts &&
                  operationalLeaseParams.multipleDiscounts.length > 0
                "
              >
                <div
                  v-for="discount in operationalLeaseParams.multipleDiscounts"
                  :key="discount.id"
                  class="applied-discount-item"
                >
                  <span class="discount-description">{{ discount.description }}</span>
                  <span v-if="discount.type === 'amount'" class="discount-tax-type"
                    >{{ discount.taxType }}.</span
                  >
                  <span class="discount-value">
                    <span v-if="discount.type === 'amount'">€</span>
                    <span v-else>%</span>
                    {{ formatDiscountValue(discount.value) }}
                  </span>
                </div>
              </div>
              <span v-else>-</span>
            </dd>
          </dl>
        </div>
      </div>
      <lease-overview :lease-type="leaseTypes.OPERATIONAL_LEASE" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import constants from '@/utils/constants'
import CurrencyHelper from '@/helpers/currencyHelper'
import LeaseOverview from '@/components/generic/container/FinancialServices/Lease/LeaseOverview/LeaseOverview.vue'

export default {
  components: { LeaseOverview },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      CurrencyHelper: CurrencyHelper,
      leaseTypes: constants.leaseTypes,
    }
  },
  computed: {
    ...mapGetters({
      quote: 'quote/get',
      latestVwpfsPrivateLeaseCall: 'quote/latestVwpfsPrivateLeaseCall',
      showPrivateLease: 'quote/showVwpfsPrivateLeasePdf',
      latestVwpfsOperationalLeaseCall: 'quote/latestVwpfsOperationalLeaseCall',
      showOperationalLease: 'quote/showVwpfsOperationalLeasePdf',
    }),
    privateLeaseResponse() {
      return this.latestVwpfsPrivateLeaseCall?.response
    },
    privateLeasePrice() {
      const rate = this.privateLeaseResponse?.pricingData?.calculatedProduct?.rate
      return this.privateLeaseResponse?.pricingData
        ? this.i18n.n(rate?.amountAfterPrePayment ?? rate?.amount, 'currencyRounded')
        : '-'
    },
    rateAmount() {
      const rate = this.privateLeaseResponse?.pricingData?.calculatedProduct?.rate
      return this.privateLeaseResponse?.pricingData
        ? this.i18n.n(parseFloat(rate?.amount), 'currencyRounded')
        : '-'
    },
    ratePrePaymentMonthly() {
      return (
        parseFloat(this.privateLeaseResponse?.pricingData?.calculatedProduct?.rate?.prePayment) || 0
      )
    },
    hasMonthlyPrePayment() {
      return this.ratePrePaymentMonthly > 0
    },
    formattedRatePrePaymentMonthly() {
      return this.i18n.n(this.ratePrePaymentMonthly, 'currencyRounded')
    },
    privateLeaseDiscounts() {
      return this.privateLeaseResponse?.pricingData?.calculatedProduct?.discounts
    },
    privateLeaseParams() {
      return this.latestVwpfsPrivateLeaseCall
    },
    selectedOwnRisk() {
      return this.i18n.n(
        parseInt(
          constants.PRIVATE_LEASE_OWN_RISK_LEVELS.find(
            (x) => x.value === this.privateLeaseParams?.ownRiskLevel
          )?.name
        ),
        'currencyRounded'
      )
    },
    // Operational Lease computed properties
    operationalLeaseResponse() {
      return this.latestVwpfsOperationalLeaseCall?.response
    },
    operationalLeasePrice() {
      return this.operationalLeaseResponse?.pricingData
        ? this.i18n.n(
            this.operationalLeaseResponse?.pricingData?.calculatedProduct?.rate?.amount,
            'currencyRounded'
          )
        : '-'
    },
    operationalLeaseDiscounts() {
      return this.operationalLeaseResponse?.pricingData?.calculatedProduct?.discounts
    },
    operationalLeaseParams() {
      return this.latestVwpfsOperationalLeaseCall
    },
  },
  methods: {
    formatDiscountValue(value) {
      return new Intl.NumberFormat('nl-NL', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    },
    retrievePrivateLeaseLabel(labelName, params) {
      return this.i18n.t(`/financialservices/lease/modal/${labelName}`, params)
    },
  },
}
</script>

<style lang="scss" scoped>
.vcr-private-lease-details,
.vcr-operational-lease-details {
  :deep(table) {
    font-size: inherit;
  }
  &__sections {
    display: flex;
    flex-wrap: wrap;
  }

  &__section {
    flex-basis: 50%;

    dl {
      display: flex;
      flex-wrap: wrap;

      > dt,
      > dd {
        flex: 0 0 50%;
      }
    }
  }

  .vcr-lease-total-highlight {
    font-weight: bold;
  }

  .applied-discount-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    margin-bottom: 0.5rem;
    background-color: #f8f9fa;
    border: 1px solid #e0e0e0;
    border-radius: 4px;

    .discount-description {
      flex: 1;
      font-weight: 500;
      color: #333;
    }

    .discount-tax-type {
      color: #666;
      font-size: 0.875rem;
    }

    .discount-value {
      font-weight: bold;
      color: #333;
      min-width: 80px;
      text-align: right;
    }
  }
}
</style>
